import { useEffect, useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { ArrowLeft, Loader2, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { trackEvent } from '@/analytics/AnalyticsTracker';
import { AuthModal } from '@/components/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Typography } from '@/components/ui/typography/Typography';
import {
  LegalDocumentKey,
  legalDocumentPath,
} from '@/constants/legalDocuments';
import { useAuth } from '@/hooks/useAuth';
import { useBlockedCountry } from '@/hooks/useBlockedCountry';
import { useWaaSAuth } from '@/hooks/useWaaSAuth';
import { cn } from '@/lib/utils';
import { ConflictAccountType, type User } from '@/types/auth';
import { EmailCodeStep } from './EmailCodeStep';
import { EmailConflictNotice } from './EmailConflictNotice';
import { loginDestination } from './loginDestination';
import {
  LOGIN_DEV_LINK,
  LOGIN_GOOGLE_BUTTON,
  LOGIN_OUTLINE_BUTTON,
  LOGIN_PRIMARY_BUTTON,
} from './loginStyles';
import { useBackendEmailOtp } from './useBackendEmailOtp';

/** How long before "Resend" for a Sequence code (Sequence sends; we only pace the button). */
const SEQUENCE_RESEND_SECONDS = 60;
/** GIS draws its own button at a fixed pixel width (max 400); fit it to the panel. */
const GOOGLE_BUTTON_MAX_WIDTH = 400;
const GOOGLE_BUTTON_FALLBACK_WIDTH = 280;

export type LoginMethod =
  | 'google'
  | 'email'
  | 'blocked_google'
  | 'blocked_email'
  | 'email_code_dev'
  | 'password_dev';

interface LoginPanelProps {
  /** Where it sits: the full /login page, or a dialog over the page (S3). */
  variant: 'page' | 'dialog';
  /** Where to go after a login that leaves the site (backend Google redirect). */
  returnTo?: string | null;
  /** Called after a login completed in the panel, with the user when known. */
  onSuccess?: (user: User | null) => void;
}

type Step = 'buttons' | 'email' | 'code';

const emailSchema = z.string().trim().email();

/**
 * The element's width, kept current on resize, so Google's fixed-width button fits the panel.
 * A callback ref, so it re-measures when the slot mounts again (back from the email step).
 */
function useElementWidth<T extends HTMLElement>(): [
  (el: T | null) => void,
  number,
] {
  const [el, setEl] = useState<T | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!el) return;
    const measure = () =>
      setWidth(Math.floor(el.getBoundingClientRect().width));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);
  return [setEl, width];
}

function useCountdown(): [number, (seconds: number) => void] {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (left <= 0) return;
    const timer = setTimeout(() => setLeft(s => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [left]);
  return [left, setLeft];
}

/**
 * The web login, laid out like the app's login screen (Espotz-docs#2038): Continue with Google,
 * Continue with Email → email field → 6-digit code, all inside the panel.
 * - Normal regions: headless Sequence WaaS (`useWaaSAuth`), as the app does.
 * - Sequence-blocked regions: Espotz's own Google redirect + email code, as the app's
 *   BlockedRegionAuthButtons.
 * - Dev only: "Can't sign in? Use an email code" (the Espotz email code) and email + password.
 * The page or dialog around it decides where to go once the session exists.
 */
export function LoginPanel({ variant, returnTo, onSuccess }: LoginPanelProps) {
  const { t } = useTranslation('auth');
  const { loginWithGoogle: backendGoogleRedirect } = useAuth();
  const { isBlocked, isLoading: isProbingRegion } = useBlockedCountry();
  const waas = useWaaSAuth();
  const otp = useBackendEmailOtp();
  const isProd = import.meta.env.VITE_APP_ENV === 'production';

  /** Dev fallback: use the Espotz email code even where Sequence works. */
  const [forceBackendEmail, setForceBackendEmail] = useState(false);
  const [step, setStep] = useState<Step>('buttons');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [sequenceResendLeft, startSequenceResend] = useCountdown();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  /**
   * From the moment Google hands back an account until the panel goes away: Sequence + the
   * Espotz session take a few seconds, and the buttons must not look idle meanwhile.
   */
  const [isSigningIn, setIsSigningIn] = useState(false);
  const backendEmail = isBlocked || forceBackendEmail;
  const [googleSlotRef, googleSlotWidth] = useElementWidth<HTMLDivElement>();

  useEffect(() => {
    trackEvent('login_viewed', {
      surface: variant === 'page' ? 'login_page' : 'dialog',
    });
  }, [variant]);

  // A Sequence code was requested: move to the code step.
  useEffect(() => {
    if (waas.isEmailCodeRequired) {
      setIsSendingCode(false);
      setStep('code');
    }
  }, [waas.isEmailCodeRequired]);

  // The email belongs to another method: back to the buttons, where the notice points to it.
  const { emailConflict } = waas;
  useEffect(() => {
    if (!emailConflict) return;
    setIsSendingCode(false);
    setIsSigningIn(false);
    setForceBackendEmail(false);
    setStep('buttons');
    if (emailConflict.accountType === ConflictAccountType.EMAIL)
      setEmail(emailConflict.email);
  }, [emailConflict]);

  const clicked = (method: LoginMethod) =>
    trackEvent('login_method_clicked', { method });
  const succeeded = (
    method: LoginMethod,
    user: User | null,
    isNewUser?: boolean,
  ) => {
    trackEvent('login_succeeded', { method, isNewUser: Boolean(isNewUser) });
    onSuccess?.(user);
  };

  /** Starts (or restarts, for Resend) a Sequence email sign-in; resolves when it completes. */
  const startSequenceEmail = (address: string) => {
    setIsSendingCode(true);
    startSequenceResend(SEQUENCE_RESEND_SECONDS);
    void waas.loginWithEmail(address).then(result => {
      setIsSendingCode(false);
      if (result) succeeded('email', result.user, result.isNewUser);
      else setStep(current => (current === 'code' ? 'email' : current));
    });
  };

  const submitEmail = async () => {
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setEmailError(true);
      return;
    }
    setEmailError(false);
    const address = parsed.data;
    if (backendEmail) {
      clicked(isBlocked ? 'blocked_email' : 'email_code_dev');
      if (await otp.send(address)) setStep('code');
      return;
    }
    clicked('email');
    startSequenceEmail(address);
  };

  /** From the email field back to Google / Email, out of the dev email-code mode too. */
  const backToButtons = () => {
    if (isSendingCode && !backendEmail) waas.cancelEmailLogin();
    setIsSendingCode(false);
    setForceBackendEmail(false);
    setEmailError(false);
    setStep('buttons');
  };

  const backFromCode = () => {
    if (!backendEmail) waas.cancelEmailLogin();
    setIsSendingCode(false);
    setStep('email');
  };

  const googleBackend = () => {
    clicked('blocked_google');
    setIsSigningIn(true);
    // The backend's Google login leaves the site and comes back to `/`; App's redirect effect
    // brings the user on from there.
    const destination = loginDestination(returnTo);
    if (destination !== '/')
      sessionStorage.setItem('pendingRedirect', destination);
    backendGoogleRedirect();
  };

  const title = (
    <div className='text-center'>
      <Typography variant='h3' fontWeight={700}>
        {t('login.title')}
      </Typography>
      <Typography variant='p3' color='muted' className='mt-1'>
        {t('login.subtitle')}
      </Typography>
    </div>
  );

  if (step === 'code') {
    const trimmed = email.trim();
    return (
      <section
        aria-label={t('login.a11y.panel')}
        className='flex flex-col gap-6'
        data-testid='login-panel'
      >
        {backendEmail ? (
          <EmailCodeStep
            email={trimmed}
            isSubmitting={otp.isLoading}
            resendSecondsLeft={otp.resendSecondsLeft}
            onResend={() => void otp.send(trimmed)}
            onBack={backFromCode}
            onSubmit={async code => {
              const user = await otp.verify(trimmed, code);
              if (!user) return false;
              succeeded(isBlocked ? 'blocked_email' : 'email_code_dev', user);
              return true;
            }}
          />
        ) : (
          <EmailCodeStep
            email={trimmed}
            isSubmitting={waas.isLoading}
            resendSecondsLeft={sequenceResendLeft}
            onResend={() => startSequenceEmail(trimmed)}
            onBack={backFromCode}
            onSubmit={waas.submitEmailCode}
          />
        )}
      </section>
    );
  }

  return (
    <section
      aria-label={t('login.a11y.panel')}
      className='flex flex-col gap-6'
      data-testid='login-panel'
    >
      {title}

      {isProbingRegion ? (
        <div className='flex justify-center py-3'>
          <Loader2
            className='h-5 w-5 animate-spin text-muted-foreground'
            aria-hidden
          />
        </div>
      ) : isSigningIn ? (
        <div
          role='status'
          className='flex flex-col items-center gap-3 py-6'
          data-testid='login-signing-in'
        >
          <Loader2
            className='h-8 w-8 animate-spin text-[var(--brand-primary)]'
            aria-hidden
          />
          <Typography variant='p3' color='muted'>
            {t('login.signingIn')}
          </Typography>
        </div>
      ) : (
        <div className='flex flex-col gap-3'>
          {step === 'email' ? (
            <>
              <form
                className='flex flex-col gap-2'
                noValidate
                onSubmit={event => {
                  event.preventDefault();
                  void submitEmail();
                }}
                data-testid='login-email-form'
              >
                <Label htmlFor='login-email-input'>
                  {t('waasAuth.emailLabel')}
                </Label>
                <Input
                  id='login-email-input'
                  type='email'
                  autoComplete='email'
                  autoFocus
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                  placeholder={t('waasAuth.emailPlaceholder')}
                  aria-invalid={emailError}
                  className='h-11 rounded-xl border-white/15 bg-white/5'
                  data-testid='login-email-input'
                />
                {emailError && (
                  <Typography variant='p4' color='danger' role='alert'>
                    {t('waasAuth.invalidEmail')}
                  </Typography>
                )}
                <Button
                  type='submit'
                  className={cn(LOGIN_PRIMARY_BUTTON, 'mt-2 h-11')}
                  disabled={isSendingCode || otp.isLoading || waas.isLoading}
                  data-testid='login-email-send'
                >
                  {isSendingCode || otp.isLoading ? (
                    <Loader2 className='h-4 w-4 animate-spin' aria-hidden />
                  ) : (
                    t('waasAuth.sendCode')
                  )}
                </Button>
              </form>
              <button
                type='button'
                onClick={backToButtons}
                className='mx-auto flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-white'
                data-testid='login-email-back'
              >
                <ArrowLeft className='h-4 w-4' aria-hidden />
                <Typography as='span' variant='p4' className='text-current'>
                  {t('login.otherWays')}
                </Typography>
              </button>
            </>
          ) : (
            <>
              {emailConflict && (
                <EmailConflictNotice
                  conflict={emailConflict}
                  onDismiss={waas.clearEmailConflict}
                  onUseEmail={() => {
                    waas.clearEmailConflict();
                    setStep('email');
                  }}
                />
              )}

              {/* Google */}
              {isBlocked ? (
                <button
                  type='button'
                  className={LOGIN_GOOGLE_BUTTON}
                  onClick={googleBackend}
                  data-testid='login-google-blocked'
                >
                  <GoogleMark />
                  <Typography
                    as='span'
                    variant='p3'
                    fontWeight={600}
                    className='text-current'
                  >
                    {t('waasAuth.continueWithGoogle')}
                  </Typography>
                </button>
              ) : (
                <div
                  ref={googleSlotRef}
                  className={cn(
                    'flex justify-center rounded-full',
                    emailConflict?.accountType === ConflictAccountType.GOOGLE &&
                      'ring-2 ring-amber-400/70 ring-offset-2 ring-offset-[#161616]',
                  )}
                  data-testid='login-google'
                >
                  <GoogleLogin
                    onSuccess={credential => {
                      clicked('google');
                      setIsSigningIn(true);
                      void waas.loginWithGoogle(credential).then(result => {
                        // On success stay "signing in": the page or dialog moves on next.
                        if (result)
                          succeeded('google', result.user, result.isNewUser);
                        else setIsSigningIn(false);
                      });
                    }}
                    onError={() => toast.error(t('waasAuth.error.loginFailed'))}
                    theme='outline'
                    size='large'
                    shape='pill'
                    text='continue_with'
                    width={Math.min(
                      googleSlotWidth || GOOGLE_BUTTON_FALLBACK_WIDTH,
                      GOOGLE_BUTTON_MAX_WIDTH,
                    )}
                  />
                </div>
              )}

              <button
                type='button'
                className={LOGIN_OUTLINE_BUTTON}
                onClick={() => {
                  waas.clearEmailConflict();
                  setStep('email');
                }}
                data-testid='login-email'
              >
                <Mail className='h-4 w-4' aria-hidden />
                <Typography
                  as='span'
                  variant='p3'
                  fontWeight={600}
                  className='text-current'
                >
                  {t('waasAuth.continueWithEmail')}
                </Typography>
              </button>

              {/* Dev / QA only, as in the app */}
              {!isProd && (
                <div className='mt-1 flex flex-col items-center gap-1 border-t border-white/10 pt-3'>
                  {!isBlocked && (
                    <button
                      type='button'
                      className={LOGIN_DEV_LINK}
                      onClick={() => {
                        setForceBackendEmail(true);
                        setStep('email');
                      }}
                      data-testid='login-email-code-dev'
                    >
                      <Typography
                        as='span'
                        variant='p4'
                        className='text-current'
                      >
                        {t('login.troubleSigningIn')}
                      </Typography>
                    </button>
                  )}
                  <button
                    type='button'
                    className={LOGIN_DEV_LINK}
                    onClick={() => {
                      clicked('password_dev');
                      setShowPasswordModal(true);
                    }}
                    data-testid='login-password-dev'
                  >
                    <Typography as='span' variant='p4' className='text-current'>
                      {t('login.usePasswordDev')}
                    </Typography>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <Typography variant='p4' color='muted' className='text-center'>
        {t('login.termsPrefix')}{' '}
        <Typography
          type='link'
          to={legalDocumentPath(LegalDocumentKey.TERMS_OF_SERVICE)}
          as='span'
          variant='p4'
          className='underline'
        >
          {t('login.termsLink')}
        </Typography>{' '}
        {t('login.and')}{' '}
        <Typography
          type='link'
          to={legalDocumentPath(LegalDocumentKey.PRIVACY_POLICY)}
          as='span'
          variant='p4'
          className='underline'
        >
          {t('login.privacyLink')}
        </Typography>
      </Typography>

      {!isProd && (
        <AuthModal
          isOpen={showPasswordModal}
          onClose={() => setShowPasswordModal(false)}
          initialMode='login'
        />
      )}
    </section>
  );
}

/** Google's "G", for the blocked-region button (GIS draws its own in normal regions). */
function GoogleMark() {
  return (
    <svg
      viewBox='0 0 48 48'
      className='h-[18px] w-[18px]'
      aria-hidden
      focusable='false'
    >
      <path
        fill='#EA4335'
        d='M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z'
      />
      <path
        fill='#4285F4'
        d='M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z'
      />
      <path
        fill='#FBBC05'
        d='M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z'
      />
      <path
        fill='#34A853'
        d='M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z'
      />
    </svg>
  );
}
